import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Checkbox,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Braces, Plus, Trash2 } from 'lucide-react';
import {
  taxonomyFieldTemplatesQueryOptions,
  useBulkDeleteFieldTemplates,
  useCreateFieldTemplatesBulk,
  useDeleteFieldTemplate,
  type TaxonomyFieldTemplate,
} from '@/entities/taxonomy-field-template';
import { canBindList, TAXONOMY_FIELD_TYPE_LABELS, taxonomyLevelLabel } from '@/entities/taxonomy';
import {
  taxonomyListsQueryOptions,
  useSeedStandardLists,
  type TaxonomyListSummary,
} from '@/entities/taxonomy-list';
import { useCurrentUser, usePermissions } from '@/entities/user';
import { PageTransition } from '@/shared/ui/animations';
import {
  TaxonomyEmptyState,
  TaxonomyErrorState,
  TaxonomyNoResultsState,
} from '../components/TaxonomyStates/TaxonomyStates';
import { MonksFlowMarkIcon } from '@/shared/ui/Icons';
import { microInteraction } from '@/shared/ui/MicroInteractions';
import { FormDialog } from '@/shared/ui/FormDialog';
import { SearchInput } from '@/shared/ui/SearchInput';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { ROW_WITHOUT_FOCUS_RING, redesignTableStyles } from '@/shared/ui/data-table';
import {
  standardFieldLevel,
  type StandardField,
} from '../TaxonomyDetail/components/FieldLibraryModal/standard-fields';
import {
  findStandardList,
  standardFieldListRef,
  standardListKeysFor,
} from '../TaxonomyDetail/components/FieldLibraryModal/standard-field-lists';
import { AddStandardFieldsModal } from './components/AddStandardFieldsModal/AddStandardFieldsModal';
import { FieldActionMenu } from './components/FieldActionMenu/FieldActionMenu';
import { FieldHistoryDrawer } from './components/FieldHistoryDrawer/FieldHistoryDrawer';
import { FieldTemplateModal } from './components/FieldTemplateModal/FieldTemplateModal';
import { standardKeyOf } from './lib/standardKey';

/** Placeholder rows while the library loads, matching the other two tabs. */
const SKELETON_ROWS = Array.from({ length: 5 }, (_, i) => i);
/** Name, Type, Field type, Level, Scope, Options, Category, Actions — plus Checkbox for a writer. */
const SKELETON_CELLS = Array.from({ length: 8 }, (_, i) => i);
const SKELETON_CELLS_WRITABLE = Array.from({ length: 9 }, (_, i) => i);

/**
 * Fields tab — the org's reusable field library.
 *
 * A template is COPY-on-add: dropping one into a schema creates an independent
 * field, so editing here never rewrites existing schemas. That's the deliberate
 * split from Lists, which DO propagate.
 */
export function TaxonomyFields() {
  const { t } = useTranslation('knowledge');
  // Library writes are manage-guarded server-side; mirror that here.
  const { canManageTaxonomy } = usePermissions();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<TaxonomyFieldTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [historyFor, setHistoryFor] = useState<TaxonomyFieldTemplate | null>(null);
  // Owned by the page, not the row: deleting drops the row, and a Radix dialog
  // unmounted while open leaves `body { pointer-events: none }` behind, which
  // freezes the entire page.
  const [deleting, setDeleting] = useState<TaxonomyFieldTemplate | null>(null);
  const [standardOpen, setStandardOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const query = useQuery(taxonomyFieldTemplatesQueryOptions());
  const listsQuery = useQuery(taxonomyListsQueryOptions());
  const currentUser = useCurrentUser();
  const seed = useCreateFieldTemplatesBulk();
  // Silent: seeding lists here is a prerequisite for binding, not the action
  // the user took — its "nothing to add" toast would be noise on this tab.
  const seedLists = useSeedStandardLists({ silent: true });

  const templates = useMemo<TaxonomyFieldTemplate[]>(() => query.data ?? [], [query.data]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter(
      (t) => t.name.toLowerCase().includes(term) || t.category.toLowerCase().includes(term),
    );
  }, [templates, debouncedSearch]);

  /** Catalog keys the library already holds, so the seed never re-adds them. */
  const seededKeys = useMemo(
    () => new Set(templates.map(standardKeyOf).filter((k): k is string => !!k)),
    [templates],
  );

  /**
   * Seeds the library from the catalog the frontend already ships — an explicit
   * action rather than magic auto-seeding, so the org stays in control of what
   * lands in its library.
   *
   * Only the FIELD entries that are actually missing are sent; the backend skips
   * known keys too, so a double-click can't clone the catalog.
   *
   * The Standard Lists the chosen fields need are seeded first, because the two
   * catalogs describe the same vocabularies: a standard field should arrive
   * bound to its governed list rather than carrying a private copy of the
   * options. Only the needed keys are requested — seeding the whole catalog
   * here would undo a library the org had deliberately curated, which is exactly
   * what the Lists picker was built to avoid.
   *
   * A failed seed is not fatal. The fields still land, carrying the inline values
   * they ship with, which is the degradation `standardFieldListRef` documents —
   * so this must not abort the whole action.
   */
  const addStandardFields = async (chosen: StandardField[]) => {
    if (chosen.length === 0) return;

    const existing = listsQuery.data ?? [];
    // Filter on the FIELD key, not the list key: `findStandardList` resolves a
    // field to the list governing it, so a field whose list the org already has
    // contributes nothing to seed.
    const needed = standardListKeysFor(
      chosen.filter((f) => !findStandardList(f.key, existing)).map((f) => f.key),
    );

    let seededLists: TaxonomyListSummary[] = [];
    if (needed.length > 0) {
      try {
        seededLists = await seedLists.mutateAsync(needed);
      } catch {
        // The mutation raises its own toast. Falling through leaves the fields
        // bound to nothing and carrying their inline values.
      }
    }
    // The mutation returns only what it CREATED, so fold in what was already
    // there to resolve bindings for a library that was seeded earlier.
    const allLists = [...existing, ...seededLists];

    seed.mutate(
      chosen.map((f) => {
        const listRef = standardFieldListRef(f.key, allLists);
        return {
          name: f.name,
          category: f.category,
          group: f.group,
          type: f.type,
          level: standardFieldLevel(f.key),
          libraryKey: f.key,
          ...(f.format ? { format: f.format } : {}),
          // Only a dropdown can carry a binding; see `canBindList`.
          ...(listRef && canBindList(f.type) ? { listRef } : {}),
          // Kept even when bound: unlinking a column restores these, and they
          // are the fallback while a list is unavailable.
          values: f.values.map((v) => ({ ...v })),
        };
      }),
      { onSuccess: () => setStandardOpen(false) },
    );
  };

  /** Names the list a bound template pulls from, rather than a generic label. */
  const listNameById = useMemo(
    () => new Map((listsQuery.data ?? []).map((l) => [l.id, l.name])),
    [listsQuery.data],
  );

  const pendingSeed = seed.isPending || seedLists.isPending;

  // Selection is scoped to visible rows so a search filter can't hide something
  // a "select all" then deletes.
  const visibleSelected = useMemo(
    () => filtered.filter((t) => selectedIds.has(t.id)),
    [filtered, selectedIds],
  );
  const allVisibleSelected = filtered.length > 0 && visibleSelected.length === filtered.length;

  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelectedIds(allVisibleSelected ? new Set() : new Set(filtered.map((t) => t.id)));

  const bulkDelete = useBulkDeleteFieldTemplates({
    onSuccess: () => {
      setBulkDeleteOpen(false);
      setSelectedIds(new Set());
    },
  });

  const removeOne = useDeleteFieldTemplate();

  const isEmpty = !query.isLoading && !query.isError && templates.length === 0;
  /**
   * Filtered to nothing is NOT the same as empty, and gating only on the
   * unfiltered total meant a search matching no rows rendered a bare column
   * header over an empty body — with no `role="status"` either, so assistive
   * technology was told nothing at all.
   */
  const showNoResults =
    !query.isLoading && !query.isError && templates.length > 0 && filtered.length === 0;

  return (
    <>
      <PageTransition variant="fade" className="flex min-h-0 flex-1 flex-col">
        {!isEmpty && !query.isError && (
          <p className="px-section pt-3 text-sm text-muted-foreground">
            {t('taxonomy.taxonomyFields.copyModelNote')}
          </p>
        )}
        {!isEmpty && (
          <div className="flex items-center justify-between gap-3 px-section py-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('taxonomy.taxonomyFields.searchFields')}
              aria-label={t('taxonomy.taxonomyFields.searchFields2')}
              className="w-72"
            />
            <div className="flex items-center gap-2">
              {canManageTaxonomy && visibleSelected.length > 0 && (
                <Button
                  variant="destructive"
                  size="mini"
                  className="h-8"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="size-3.5" />
                  {t('taxonomy.taxonomyFields.removeFields', { count: visibleSelected.length })}
                </Button>
              )}
              {canManageTaxonomy && (
                <>
                  <Button
                    variant="outline"
                    size="mini"
                    className="h-8"
                    onClick={() => setStandardOpen(true)}
                    title={t('taxonomy.taxonomyFields.chooseWhichMonksflowStandardFields')}
                  >
                    <MonksFlowMarkIcon className="h-3.5 w-[1.3rem]" />
                    {t('taxonomy.taxonomyFields.addStandardFields')}
                  </Button>
                  <Button size="mini" className="h-8" onClick={() => setCreating(true)}>
                    <Plus className="size-3.5" />
                    {t('taxonomy.taxonomyFields.newField')}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {query.isError ? (
          <TaxonomyErrorState
            title={t('taxonomy.taxonomyStates.couldnTLoadFields')}
            onRetry={() => void query.refetch()}
          />
        ) : isEmpty ? (
          <TaxonomyEmptyState
            icon={<Braces className="size-6 text-muted-foreground" aria-hidden />}
            title={t('taxonomy.taxonomyFields.noReusableFieldsYet')}
            description={t('taxonomy.taxonomyFields.saveFieldDefinitionOnceType')}
          >
            {canManageTaxonomy && (
              <div className="flex items-center gap-2">
                <Button size="mini" onClick={() => setStandardOpen(true)}>
                  <MonksFlowMarkIcon className="h-3.5 w-[1.3rem]" />
                  {t('taxonomy.taxonomyFields.addStandardFields')}
                </Button>
                <Button variant="outline" size="mini" onClick={() => setCreating(true)}>
                  <Plus className="size-3.5" />
                  {t('taxonomy.taxonomyFields.newField')}
                </Button>
              </div>
            )}
          </TaxonomyEmptyState>
        ) : showNoResults ? (
          <TaxonomyNoResultsState
            title={t('taxonomy.taxonomyStates.noFieldsFound')}
            description={t('taxonomy.taxonomyStates.noFieldsMatchYourSearch')}
            onClear={() => setSearch('')}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto px-section pb-section">
            {/* Same Datatable primitives as Lists and Schemas — category is a
                column now rather than a grouping heading, which makes the three
                tabs read identically. */}
            <Datatable
              aria-busy={query.isLoading || undefined}
              className={redesignTableStyles.table}
            >
              <DatatableHeader>
                <DatatableRow className={ROW_WITHOUT_FOCUS_RING}>
                  {canManageTaxonomy && (
                    <DatatableHead scope="col" className="w-10">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={toggleAllVisible}
                        aria-label={
                          allVisibleSelected
                            ? t('taxonomy.taxonomyFields.clearSelection')
                            : t('taxonomy.taxonomyFields.selectAllFields')
                        }
                      />
                    </DatatableHead>
                  )}
                  <DatatableHead scope="col">{t('taxonomy.taxonomyFields.name')}</DatatableHead>
                  <DatatableHead scope="col" className="w-40">
                    {t('taxonomy.taxonomyFields.type')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-32">
                    {t('taxonomy.taxonomyFields.fieldType')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-28">
                    {t('taxonomy.taxonomyFields.level')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-32">
                    {t('taxonomy.taxonomyFields.scope')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-44">
                    {t('taxonomy.taxonomyFields.options')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-36">
                    {t('taxonomy.taxonomyFields.category')}
                  </DatatableHead>
                  <DatatableHead scope="col" className="w-13">
                    <span className="sr-only">{t('taxonomy.taxonomyFields.actions')}</span>
                  </DatatableHead>
                </DatatableRow>
              </DatatableHeader>
              <DatatableBody>
                {query.isLoading
                  ? SKELETON_ROWS.map((i) => (
                      <DatatableRow key={i} className="h-[72px]">
                        {(canManageTaxonomy ? SKELETON_CELLS_WRITABLE : SKELETON_CELLS).map((c) => (
                          <DatatableCell key={c}>
                            <Skeleton className="h-4 w-full" />
                          </DatatableCell>
                        ))}
                      </DatatableRow>
                    ))
                  : filtered.map((tpl) => {
                      const standardKey = standardKeyOf(tpl);
                      return (
                        <DatatableRow
                          key={tpl.id}
                          interactive={canManageTaxonomy}
                          className={`h-[72px] ${ROW_WITHOUT_FOCUS_RING}`}
                          onClick={canManageTaxonomy ? () => setEditing(tpl) : undefined}
                        >
                          {canManageTaxonomy && (
                            <DatatableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedIds.has(tpl.id)}
                                onCheckedChange={() => toggleRow(tpl.id)}
                                aria-label={t('taxonomy.taxonomyFields.selectField', {
                                  name: tpl.name,
                                })}
                              />
                            </DatatableCell>
                          )}
                          <DatatableCell>
                            <span className="text-sm font-medium text-foreground">{tpl.name}</span>
                            {tpl.required && <span className="ml-1 text-destructive">*</span>}
                            {tpl.description && (
                              <div className="truncate text-xs text-muted-foreground">
                                {tpl.description}
                              </div>
                            )}
                          </DatatableCell>
                          {/* Provenance, matching the Lists "Type" column. */}
                          <DatatableCell>
                            {!standardKey ? (
                              <Badge
                                variant="secondary"
                                roundness="full"
                                className={microInteraction.statusBadge}
                              >
                                {t('taxonomy.taxonomyFields.custom')}
                              </Badge>
                            ) : tpl.customized ? (
                              <Badge
                                variant="secondary"
                                roundness="full"
                                className={microInteraction.statusBadge}
                              >
                                {t('taxonomy.taxonomyFields.editedStandard')}
                              </Badge>
                            ) : (
                              <Badge
                                variant="default"
                                roundness="full"
                                className={microInteraction.statusBadge}
                              >
                                <MonksFlowMarkIcon className="mr-1 h-3 w-[1.1rem]" />
                                {t('taxonomy.taxonomyFields.standard')}
                              </Badge>
                            )}
                          </DatatableCell>
                          <DatatableCell>
                            <span className="text-sm text-foreground">
                              {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- a template's type is a bare string, so the label map need not hold it */}
                              {TAXONOMY_FIELD_TYPE_LABELS[tpl.type] ?? tpl.type}
                            </span>
                          </DatatableCell>
                          <DatatableCell>
                            <span className="text-sm text-foreground">
                              {tpl.level && tpl.level !== 'all'
                                ? taxonomyLevelLabel(tpl.level)
                                : t('taxonomy.taxonomyFields.all')}
                            </span>
                          </DatatableCell>
                          {/* Derived from `orgWide` — field templates have no
                              `scope` column of their own (unlike lists); a null
                              workspaceId IS the org-wide signal. */}
                          <DatatableCell>
                            <span className="text-sm text-foreground">
                              {tpl.orgWide
                                ? t('taxonomy.taxonomyFields.organization')
                                : t('taxonomy.taxonomyFields.workspace')}
                            </span>
                          </DatatableCell>
                          <DatatableCell>
                            {tpl.listRef || tpl.values.length > 0 ? (
                              <span className="text-sm text-foreground">
                                {tpl.listRef
                                  ? (listNameById.get(tpl.listRef.listId) ??
                                    t('taxonomy.taxonomyFields.sharedList'))
                                  : t('taxonomy.taxonomyFields.optionCount', {
                                      count: tpl.values.length,
                                    })}
                              </span>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </DatatableCell>
                          <DatatableCell>
                            <span className="text-sm text-foreground">{tpl.category}</span>
                          </DatatableCell>
                          <DatatableCell className="text-right">
                            <FieldActionMenu
                              template={tpl}
                              onEdit={canManageTaxonomy ? () => setEditing(tpl) : undefined}
                              onHistory={() => setHistoryFor(tpl)}
                              onDelete={canManageTaxonomy ? () => setDeleting(tpl) : undefined}
                            />
                          </DatatableCell>
                        </DatatableRow>
                      );
                    })}
              </DatatableBody>
            </Datatable>
          </div>
        )}
      </PageTransition>

      {standardOpen && (
        <AddStandardFieldsModal
          open
          existingLists={listsQuery.data ?? []}
          addedKeys={seededKeys}
          isSubmitting={pendingSeed}
          onClose={() => setStandardOpen(false)}
          onAdd={(chosen) => void addStandardFields(chosen)}
        />
      )}

      {bulkDeleteOpen && (
        <FormDialog
          open
          onOpenChange={(next) => !next && setBulkDeleteOpen(false)}
          title={t('taxonomy.taxonomyFields.removeFieldsTitle', {
            count: visibleSelected.length,
          })}
          className="flex max-h-[calc(100dvh-64px)] flex-col sm:!max-w-[28rem]"
        >
          <FormDialog.Body className="flex min-h-0 flex-col gap-3 overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.taxonomyFields.removesThemLibraryOnlySchemas')}
            </p>
            <ul className="flex flex-col gap-0.5">
              {visibleSelected.map((tpl) => (
                <li key={tpl.id} className="truncate text-xs text-muted-foreground">
                  {tpl.name}
                  <span className="ml-1 opacity-70">({tpl.category})</span>
                </li>
              ))}
            </ul>
          </FormDialog.Body>
          <FormDialog.Footer
            primary={{
              label: t('taxonomy.taxonomyFields.removeFields', {
                count: visibleSelected.length,
              }),
              onClick: () => bulkDelete.mutate(visibleSelected),
              disabled: bulkDelete.isPending,
              loading: bulkDelete.isPending,
            }}
            secondary={{
              label: t('taxonomy.taxonomyFields.cancel'),
              onClick: () => setBulkDeleteOpen(false),
            }}
          />
        </FormDialog>
      )}

      {deleting && (
        <FormDialog
          open
          onOpenChange={(next) => !next && setDeleting(null)}
          title={t('taxonomy.taxonomyFields.removeFieldTitle', { name: deleting.name })}
          className="flex flex-col sm:!max-w-[28rem]"
        >
          <FormDialog.Body>
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.taxonomyFields.removesLibraryOnlySchemasAlready')}
            </p>
          </FormDialog.Body>
          <FormDialog.Footer
            primary={{
              label: t('taxonomy.taxonomyFields.removeFromLibrary'),
              onClick: () =>
                removeOne.mutate(deleting, {
                  onSuccess: () => {
                    // Undo can bring the row back, and it should not return still ticked.
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      next.delete(deleting.id);
                      return next;
                    });
                    setDeleting(null);
                  },
                }),
              disabled: removeOne.isPending,
              loading: removeOne.isPending,
            }}
            secondary={{
              label: t('taxonomy.taxonomyFields.cancel'),
              onClick: () => setDeleting(null),
            }}
          />
        </FormDialog>
      )}

      {historyFor && (
        <FieldHistoryDrawer
          open
          onOpenChange={(next) => !next && setHistoryFor(null)}
          template={historyFor}
          currentUserId={currentUser?.id}
          currentUserName={currentUser?.name}
        />
      )}

      {(creating || editing) && (
        <FieldTemplateModal
          open
          template={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

TaxonomyFields.displayName = 'TaxonomyFields';
